/**
 * @fileoverview Componente Atom para mostrar pantalla vacía en listas del Inbox.
 * @module EmptyScreenListInboxMC
 * @category Component - Atom
 * @since 3.1.0
 * @version 3.1.0
 *
 * @description
 * Componente atómico reutilizable para mostrar un estado vacío en listas del Inbox.
 * Permite personalizar ícono, título y descripción, usando i18n por defecto.
 *
 * @component EmptyScreenListInboxMC
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.iconName='empty-inbox'] - Nombre del ícono a mostrar
 * @param {string} [props.title=''] - Título principal (opcional, usa traducción por defecto)
 * @param {string} [props.description=''] - Descripción secundaria (opcional, usa traducción por defecto)
 * @param {string} [props.iconClassName=''] - Clases adicionales para el ícono
 *
 * @returns {JSX.Element} Elemento JSX renderizado
 *
 * @example
 * <EmptyScreenListInboxMC
 *   iconName="empty-inbox"
 *   title="Sin conversaciones"
 *   description="No hay chats activos en este momento."
 * />
 *
 * @see IconMC
 */
import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import IconMC from "./IconMC";

/**
 * @typedef {Object} EmptyScreenListInboxMCProps
 * @property {string} [iconName='empty-inbox'] - Nombre del ícono a mostrar
 * @property {string} [title=''] - Título principal (opcional, usa traducción por defecto)
 * @property {string} [description=''] - Descripción secundaria (opcional, usa traducción por defecto)
 * @property {string} [iconClassName=''] - Clases adicionales para el ícono
 * @property {boolean} [showDescription=true] - Si se muestra la descripción
 * @property {string} [containerClassName=''] - Clases adicionales para el contenedor principal
 * @property {string} [innerClassName=''] - Clases adicionales para el contenedor visual interno
 */

/**
 * @param {EmptyScreenListInboxMCProps} props
 */
const EmptyScreenListInboxMC = memo(({
  iconName = 'empty-inbox',
  title = '',
  description = '',
  iconClassName = '',
  showDescription = true,
  containerClassName = '',
  innerClassName = ''
}) => {
  const { t } = useTranslation('EmptyScreensMC');

  // Memoizar valores derivados para evitar renders innecesarios
  const resolvedTitle = useMemo(() => title || t('chat_list.title'), [title, t]);
  const resolvedDescription = useMemo(() => description || t('chat_list.description'), [description, t]);
  const iconClass = useMemo(() => `tw-text-blue-500 tw-size-8 ${iconClassName}`, [iconClassName]);

  return (
    <div className={`tw-wrapper tw-h-full tw-w-full ${containerClassName}`}>
      <div className={`tw-flex tw-flex-col tw-items-center tw-justify-start tw-pt-16 tw-bg-gray-50 tw-h-full tw-w-full tw-px-2 ${innerClassName}`}>
        <div className="tw-mb-4">
          <IconMC name={iconName} className={iconClass} />
        </div>
        <p className="tw-text-blue-500 tw-text-sm tw-font-medium tw-mb-2 tw-text-center">{resolvedTitle}</p>
        {showDescription && (
          <p className="tw-text-gray-950 tw-text-sm tw-font-base tw-text-center">{resolvedDescription}</p>
        )}
      </div>
    </div>
  );
});

EmptyScreenListInboxMC.displayName = 'EmptyScreenListInboxMC';

export default EmptyScreenListInboxMC;
